import type { CgDesignerDesign } from '../../types/index.js'

export interface CgEditorSnapshot { name: string; slug: string; design: CgDesignerDesign }
export interface CgEditorDraft extends CgEditorSnapshot { expectedRevision?: string; savedAt: string }

/** A response only acknowledges the document/session actually sent. */
export class CgSaveTracker {
  session = 0
  revision = 0
  open() { this.session++; this.revision = 0 }
  edit() { this.revision++ }
  capture() { return { session: this.session, revision: this.revision } }
  accepts(token: ReturnType<CgSaveTracker['capture']>) { return token.session === this.session }
  clean(token: ReturnType<CgSaveTracker['capture']>) { return this.accepts(token) && token.revision === this.revision }
}

export function cgDraftKey(channelId: string, templateId: string | null) { return `trafficplay:cg-draft:v1:${channelId}:${templateId ?? 'new'}` }

export function readCgDraft(storage: Pick<Storage, 'getItem'>, key: string): CgEditorDraft | null {
  try {
    const draft = JSON.parse(storage.getItem(key) || 'null') as CgEditorDraft | null
    if (!draft || typeof draft.name !== 'string' || typeof draft.slug !== 'string' || !draft.design
      || !Number.isFinite(draft.design.width) || !Number.isFinite(draft.design.height) || !Array.isArray(draft.design.elements)) return null
    return draft
  } catch { return null }
}

export function cgSaveError(error: unknown): string {
  const response = error as { message?: string; body?: { details?: Array<{ path?: Array<string | number>; message?: string }> } }
  const details = response?.body?.details
  if (Array.isArray(details) && details.length) {
    return details.map(issue => `${(issue.path ?? []).join(' → ')} : ${issue.message ?? 'valeur invalide'}`).join(' ; ')
  }
  return response?.message || 'Enregistrement impossible. Votre brouillon est conservé.'
}

export function validCgDesign(design: CgDesignerDesign): { elementId?: string; message: string } | null {
  if (design.elements.length > 150) return { message: 'Maximum 150 calques par modèle.' }
  const bounds: Record<string, [number, number]> = {
    width: [320, 7680], height: [180, 4320], fps: [1, 120], durationMs: [250, 3600000], gridSize: [1, 200], safeArea: [0, 25],
  }
  for (const [key, [min, max]] of Object.entries(bounds)) {
    const value = design[key as keyof CgDesignerDesign]
    if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max)) return { message: `Composition : ${key} doit être compris entre ${min} et ${max}.` }
  }
  const elementBounds: Record<string, [number, number]> = {
    x: [-design.width, design.width * 2], y: [-design.height, design.height * 2],
    width: [1, design.width * 2], height: [1, design.height * 2], rotation: [-3600, 3600],
    opacity: [0, 1], fontSize: [1, 600], fontWeight: [100, 900], letterSpacing: [-50, 200], lineHeight: [0.5, 5],
    borderRadius: [0, 1000], borderWidth: [0, 100], shadowBlur: [0, 300], shadowX: [-500, 500], shadowY: [-500, 500],
    textStrokeWidth: [0, 100], textShadowBlur: [0, 300], textShadowX: [-500, 500], textShadowY: [-500, 500],
    delayMs: [0, 3600000], durationMs: [1, 3600000], exitDelayMs: [0, 3600000], exitDurationMs: [1, 3600000],
    videoPlaybackRate: [0.1, 4], videoHoldFrame: [0, 1000000], videoFrameRate: [1, 120], videoResumeBeforeEndMs: [0, 3600000],
    scrollSpeedPxSec: [10, 2000], scrollGapPx: [0, 2000],
  }
  for (const element of design.elements) {
    if (!element.name.trim()) return { elementId: element.id, message: 'Donnez un nom au calque.' }
    for (const [key, [min, max]] of Object.entries(elementBounds)) {
      const value = element[key as keyof typeof element]
      if (value !== undefined && (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max)) return { elementId: element.id, message: `${element.name} : ${key} doit être compris entre ${min} et ${max}.` }
    }
  }
  return null
}
